// Replace with your actual IDs
const GA_TRACKING_ID = "G-DN36MPNJ8R";
const MIXPANEL_TOKEN = "039e4f3ff53806957cbcfeac440323f9";

let isLoaded = false;
let isInitializing = false;
const eventQueue = [];

const isConfigured = () => {
    const isProduction = process.env.NODE_ENV === 'production';
    return isProduction && ((GA_TRACKING_ID && GA_TRACKING_ID !== "G-XXXXXXXXXX") ||
        (MIXPANEL_TOKEN && MIXPANEL_TOKEN !== "YOUR_MIXPANEL_TOKEN"));
};

const injectMixpanel = () => {
    (function (f, b) {
        if (!b.__SV) {
            var e, g, i, h;
            window.mixpanel = b;
            b._i = [];
            b.init = function (e, f, c) {
                function g(a, d) {
                    var b = d.split(".");
                    2 == b.length && ((a = a[b[0]]), (d = b[1]));
                    a[d] = function () {
                        a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                }
                var a = b;
                "undefined" !== typeof c ? (a = b[c] = []) : (c = "mixpanel");
                a.people = a.people || [];
                a.toString = function (a) {
                    var d = "mixpanel";
                    "mixpanel" !== c && (d += "." + c);
                    a || (d += " (stub)");
                    return d;
                };
                a.people.toString = function () {
                    return a.toString(1) + ".people (stub)";
                };
                i = "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
                for (h = 0; h < i.length; h++) g(a, i[h]);
                b._i.push([e, f, c]);
            };
            b.__SV = 1.2;
            e = f.createElement("script");
            e.type = "text/javascript";
            e.async = !0;
            e.src = "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
            g = f.getElementsByTagName("script")[0];
            g.parentNode.insertBefore(e, g);
        }
    })(document, window.mixpanel || []);
};

const flushQueue = () => {
    if (!isLoaded || eventQueue.length === 0) return;

    console.log(`[Analytics] Flushing ${eventQueue.length} queued events`);

    while (eventQueue.length > 0) {
        const { type, payload } = eventQueue.shift();
        try {
            if (type === 'event') analytics.trackEvent(payload.name, payload.properties);
            if (type === 'pageView') analytics.trackPageView(payload.path);
            if (type === 'identify') analytics.identify(payload.userId, payload.traits);
        } catch (err) {
            console.error(`[Analytics] Error flushing event ${type}:`, err);
        }
    }
};

export const analytics = {
    init: async () => {
        if (!isConfigured()) {
            console.log("[Analytics] Not configured, skipping initialization");
            return;
        }

        if (isLoaded || isInitializing) return;
        isInitializing = true;

        try {
            console.log("[Analytics] Initializing...");

            const tasks = [];

            // GA4 is handled by @next/third-parties in layout.js, so we mark it as initialized
            // We can check window.gtag if we want strict confirmation, but for now we assume it loads.

            if (MIXPANEL_TOKEN && MIXPANEL_TOKEN !== "YOUR_MIXPANEL_TOKEN") {
                // Initializing Mixpanel via CDN injection
                if (!window.mixpanel) {
                    injectMixpanel();
                }

                if (window.mixpanel) {
                    window.mixpanel.init(MIXPANEL_TOKEN, {
                        debug: false,
                        track_pageview: true,
                        persistence: "localStorage",
                        ignore_dnt: true
                    });
                    console.log("[Analytics] Mixpanel Initialized (CDN)");
                }
            }

            await Promise.all(tasks);
            isLoaded = true;
            isInitializing = false;
            flushQueue();

        } catch (error) {
            console.error("[Analytics] Init failed:", error);
            isInitializing = false;
        }
    },

    setupLazyLoad: () => {
        if (isLoaded || isInitializing) return;

        console.log("[Analytics] Setting up lazy load...");

        const loadEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove', 'click'];

        const loadHandler = () => {
            analytics.init();
            loadEvents.forEach(event => window.removeEventListener(event, loadHandler));
        };

        // Listen for interactions
        loadEvents.forEach(event => window.addEventListener(event, loadHandler, { passive: true, once: true }));

        // Fallback: Load after 4 seconds of idle time if no interaction
        setTimeout(() => {
            if (!isLoaded && !isInitializing) {
                console.log("[Analytics] Fallback timer triggered init");
                analytics.init();
            }
        }, 4000);
    },

    trackEvent: (name, properties = {}) => {
        if (!isConfigured()) return;

        if (!isLoaded) {
            eventQueue.push({ type: 'event', payload: { name, properties } });
            return;
        }

        try {
            if (window.gtag) {
                window.gtag('event', name, {
                    ...properties
                });
            } else if (typeof window.sendGAEvent === 'function') {
                // Component-based fallback if using generic exposed function
                window.sendGAEvent('event', name, properties);
            }

            if (window.mixpanel) {
                window.mixpanel.track(name, properties);
            }
        } catch (error) {
            console.error("Analytics trackEvent failed:", error);
        }
    },

    trackPageView: (path) => {
        if (!isConfigured()) return;

        if (!isLoaded) {
            eventQueue.push({ type: 'pageView', payload: { path } });
            return;
        }

        try {
            // @next/third-parties handles page views automatically, but if we need manual:
            if (window.gtag) {
                window.gtag('event', 'page_view', {
                    page_path: path
                });
            }

            if (window.mixpanel) {
                window.mixpanel.track("Page View", { path });
            }
        } catch (error) {
            console.error("Analytics trackPageView failed:", error);
        }
    },

    identify: (userId, traits = {}) => {
        if (!isConfigured()) return;

        if (!isLoaded) {
            eventQueue.push({ type: 'identify', payload: { userId, traits } });
            return;
        }

        try {
            if (window.mixin) { // Typo fix or check
                // GA doesn't strictly have "identify" in the same way, usually setUserProperties
                if (window.gtag) {
                    window.gtag('config', GA_TRACKING_ID, {
                        'user_id': userId,
                        'user_properties': traits
                    });
                }
            }

            if (window.mixpanel) {
                window.mixpanel.identify(userId);
                window.mixpanel.people.set({
                    "$email": userId,
                    ...traits
                });
            }
        } catch (error) {
            console.error("Analytics identify failed:", error);
        }
    },

    trackCardClick: (section, title) => {
        analytics.trackEvent('Card Click', {
            Section: section,
            Title: title
        });
    }
};
